import { useState, FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageSquare } from 'lucide-react';
import { fallbackService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { FallbackRequest, FallbackRequestFilters } from '../types/fallback';
import PageHeader from '../components/ui/PageHeader';
import Modal from '../components/ui/Modal';
import Pagination from '../components/ui/Pagination';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import FallbackRequestList from '../components/fallback/FallbackRequestList';
import FallbackRequestForm from '../components/fallback/FallbackRequestForm';
import FallbackRequestFilters from '../components/fallback/FallbackRequestFilters';

type ModalType = 'edit' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

const DEFAULT_LIMIT = 10;

function FallbackRequests() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingRequest, setEditingRequest] = useState<FallbackRequest | null>(null);
  const [filters, setFilters] = useState<FallbackRequestFilters>({
    page: 1,
    limit: DEFAULT_LIMIT,
  });
  const queryClient = useQueryClient();

  // Queries
  const { data: requestsResponse, isLoading } = useQuery({
    queryKey: ['fallback-requests', filters],
    queryFn: () => fallbackService.getRequests(filters),
    keepPreviousData: true,
  });

  const { data: assistants = [] } = useQuery({
    queryKey: ['assistants'],
    queryFn: () => fallbackService.getAssistants(),
  });

  const { data: whatsappSessions = [] } = useQuery({
    queryKey: ['whatsapp-sessions'],
    queryFn: () => fallbackService.getWhatsappSessions(),
  });

  // Mutations
  const updateMutation = useMutation({
    mutationFn: (data: FallbackRequest) => fallbackService.updateRequest(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fallback-requests'] });
      messageHandler.success('Solicitação atualizada com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => fallbackService.deleteRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fallback-requests'] });
      messageHandler.success('Solicitação excluída com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  // Handlers
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    if (editingRequest) {
      // Update existing request
      const data = {
        ...editingRequest,
        human_response: formData.get('human_response') as string,
        final_response: formData.get('final_response') as string,
        status: formData.get('status') as 'pending' | 'awaiting_finalization' | 'completed',
      };
      updateMutation.mutate(data);
    }
  };

  const handleDelete = (request: FallbackRequest) => {
    messageHandler.confirm(
      'Excluir Solicitação',
      'Tem certeza que deseja excluir esta solicitação de fallback?',
      () => deleteMutation.mutate(request.id)
    );
  };

  const handleFilterChange = (newFilters: FallbackRequestFilters) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({
      page: 1,
      limit: DEFAULT_LIMIT,
    });
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({
      ...prev,
      page,
    }));
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingRequest(null);
  };

  const requests = requestsResponse?.data || [];
  const pagination = requestsResponse?.pagination;
  const totalRecords = pagination?.total || 0;
  const isFiltered = Object.keys(filters).some(
    key => key !== 'page' && key !== 'limit' && filters[key as keyof FallbackRequestFilters]
  );

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Solicitações de Fallback"
          description="Gerencie as solicitações de fallback dos assistentes."
        />

        <FallbackRequestFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          assistants={assistants}
          whatsappSessions={whatsappSessions}
        />

        {/* Total Records Count */}
        <div className="mb-4 flex items-center text-sm text-gray-600 dark:text-gray-400">
          <MessageSquare className="h-4 w-4 mr-2" />
          <span>
            Total de registros: <strong>{totalRecords}</strong>
          </span>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <>
            <FallbackRequestList
              requests={requests}
              onEdit={request => {
                setEditingRequest(request);
                setModal({ type: 'edit' });
              }}
              onDelete={handleDelete}
              isFiltered={isFiltered}
            />

            {pagination && pagination.last_page > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={pagination.current_page}
                  totalPages={pagination.last_page}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}

        <Modal
          isOpen={modal.type === 'edit'}
          onClose={closeModal}
          title="Editar Solicitação de Fallback"
          size="xl"
        >
          <FallbackRequestForm
            request={editingRequest}
            assistants={assistants}
            whatsappSessions={whatsappSessions}
            onSubmit={handleSubmit}
            isLoading={updateMutation.isPending}
            onClose={closeModal}
            isEditing={true}
          />
        </Modal>
      </div>
    </div>
  );
}

export default FallbackRequests;
